import { useEffect, useState } from "react";
import { ChevronLeft, Send, ShieldAlert, Paperclip } from "lucide-react";
import client, { extractError } from "@/api";

const TYPES = ["NON_DELIVERY", "PARTIAL_DELIVERY", "QUALITY_ISSUE", "WRONG_ITEM",
                "SERVICE_NOT_PROVIDED", "QUANTITY_MISMATCH", "DOCUMENTATION_ISSUE", "OTHER"];
const STATUS_LABEL = {
  OPEN: "Open", UNDER_REVIEW: "Under review",
  AWAITING_BUYER: "Awaiting buyer", AWAITING_SELLER: "Awaiting seller",
  RESOLVED_BUYER: "Resolved (buyer)", RESOLVED_SELLER: "Resolved (seller)",
  RESOLVED_SPLIT: "Resolved (split)", REJECTED: "Rejected", CLOSED: "Closed",
};
const FILTERS = [
  { value: "", label: "All" },
  { value: "OPEN", label: "Open" },
  { value: "UNDER_REVIEW", label: "Under review" },
  { value: "AWAITING_BUYER", label: "Awaiting buyer" },
  { value: "AWAITING_SELLER", label: "Awaiting seller" },
  { value: "CLOSED", label: "Closed" },
];
const EVIDENCE_TYPES = ["DELIVERY_PROOF", "INVOICE", "PURCHASE_ORDER", "QUALITY_PHOTO", "AGREEMENT", "OTHER"];

function DisputeDetail({ id, onBack }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState("");
  const [evidenceType, setEvidenceType] = useState("OTHER");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");

  const load = async () => {
    try { const r = await client.get(`/disputes/${id}`); setD(r.data); }
    catch (err) { setError(extractError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!d) return <div className="loading-block">Loading…</div>;

  const respond = async () => {
    setBusy(true); setError(""); setMessage("");
    try { await client.post(`/disputes/${id}/respond`, { notes: response }); setResponse(""); setMessage("Response submitted."); load(); }
    catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };
  const uploadEvidence = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const fd = new FormData(); fd.append("file", file);
      await client.post(`/disputes/${id}/evidence?document_type=${evidenceType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null); setMessage("Evidence uploaded."); load();
    } catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  const canRespond = ["OPEN", "UNDER_REVIEW", "AWAITING_BUYER", "AWAITING_SELLER"].includes(d.status);

  return (
    <div className="admin-company-detail" data-testid="dispute-detail">
      <button type="button" className="link-button back" onClick={onBack}><ChevronLeft size={14} /> Back to disputes</button>
      <header className="detail-header">
        <div>
          <p className="eyebrow">{STATUS_LABEL[d.status] || d.status}</p>
          <h2>{d.dispute_type.replace(/_/g, " ")}</h2>
          <p className="muted">Your role: <strong>{d.viewer_side}</strong> · Amount in dispute: ₹{d.amount_in_dispute.toLocaleString("en-IN")}</p>
        </div>
        <div className="chip-stack">
          <span className={`status-chip status-${d.status.toLowerCase()}`}>{STATUS_LABEL[d.status]}</span>
        </div>
      </header>
      {error && <div className="form-error">{error}</div>}
      {message && <div className="form-success">{message}</div>}
      <section className="section-block">
        <p className="eyebrow">DESCRIPTION</p>
        <p>{d.description}</p>
      </section>
      {d.transaction && (
        <section className="detail-grid">
          <article><p className="eyebrow">TRANSACTION</p><p>{d.transaction.listing_title}</p></article>
          <article><p className="eyebrow">TRADE VALUE</p><p>₹{d.transaction.trade_value.toLocaleString("en-IN")}</p></article>
          <article><p className="eyebrow">TXN STATUS</p><p>{d.transaction.status}</p></article>
          <article><p className="eyebrow">SETTLEMENT</p><p>{d.transaction.settlement_status}</p></article>
        </section>
      )}
      {d.resolution_notes && (
        <section className="section-block">
          <p className="eyebrow">RESOLUTION</p>
          <p><strong>{d.resolution}</strong> — {d.resolution_notes}</p>
        </section>
      )}
      {canRespond && (
        <section className="section-block">
          <p className="eyebrow">SUBMIT A RESPONSE</p>
          <textarea rows={3} value={response} onChange={(e) => setResponse(e.target.value)} data-testid="dispute-response" placeholder="Share your side of the story…" />
          <button type="button" className="primary-button" onClick={respond} disabled={busy || response.length < 3} data-testid="dispute-respond-btn"><Send size={13} /> Submit response</button>
        </section>
      )}
      {canRespond && (
        <form className="section-block" onSubmit={uploadEvidence} data-testid="evidence-upload">
          <p className="eyebrow">UPLOAD EVIDENCE</p>
          <div className="form-grid">
            <label>Document type<select value={evidenceType} onChange={(e) => setEvidenceType(e.target.value)} data-testid="evidence-type">
              {EVIDENCE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select></label>
            <label>File<input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="evidence-file" /></label>
          </div>
          <button type="submit" className="ghost-button" disabled={busy || !file} data-testid="evidence-submit"><Paperclip size={13} /> Upload evidence</button>
        </form>
      )}
      {d.evidence?.length > 0 && (
        <section className="documents-section">
          <div className="section-heading"><div><p className="eyebrow">EVIDENCE</p><h3>Uploaded documents</h3></div></div>
          <table className="admin-table" data-testid="dispute-evidence-table">
            <thead><tr><th>Type</th><th>File</th><th>Size</th><th>Uploaded by</th><th>When</th></tr></thead>
            <tbody>{d.evidence.map((e) => (
              <tr key={e.id}><td>{e.document_type}</td><td><small>{e.filename}</small></td>
                <td><small>{Math.round(e.size_bytes / 1024)} KB</small></td>
                <td><small>{e.uploaded_by_company_id.slice(0, 8)}</small></td>
                <td><small>{new Date(e.created_at).toLocaleString()}</small></td></tr>
            ))}</tbody>
          </table>
        </section>
      )}
      {d.audit_log?.length > 0 && (
        <section className="documents-section">
          <div className="section-heading"><div><p className="eyebrow">HISTORY</p><h3>Audit trail</h3></div></div>
          <ul className="audit-list" data-testid="dispute-audit">
            {d.audit_log.map((a) => (
              <li key={a.id}>
                <strong>{a.event}</strong><small>{a.previous_state || "—"} → {a.new_state || "—"}</small>
                {a.notes && <p className="muted small">{a.notes}</p>}
                <small className="muted">{a.actor_email || "system"} · {new Date(a.created_at).toLocaleString()}</small>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function RaiseDialog({ transactions, onCancel, onSubmit, busy }) {
  const [txnId, setTxnId] = useState(transactions[0]?.id || "");
  const [type, setType] = useState("NON_DELIVERY");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState(0);
  const active = transactions.find((t) => t.id === txnId);
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="raise-dispute-dialog">
      <form className="dialog-panel" onSubmit={(e) => { e.preventDefault(); onSubmit({ transaction_id: txnId, dispute_type: type, description, amount_in_dispute: Number(amount) || 0 }); }}>
        <h3>Raise a dispute</h3>
        <label>Transaction<select required value={txnId} onChange={(e) => setTxnId(e.target.value)} data-testid="dispute-txn-select">
          {transactions.map((t) => <option key={t.id} value={t.id}>{t.listing_title} · ₹{t.trade_value.toLocaleString("en-IN")}</option>)}
        </select></label>
        <label>Issue type<select value={type} onChange={(e) => setType(e.target.value)} data-testid="dispute-type-select">
          {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select></label>
        <label>Description (min 10 chars)<textarea required minLength={10} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="dispute-description" /></label>
        <label>Amount in dispute (₹)<input type="number" min={0} max={active?.trade_value || 0} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="dispute-amount" /></label>
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>Cancel</button>
          <button type="submit" className="primary-button" disabled={busy} data-testid="dispute-submit"><ShieldAlert size={14} /> Raise dispute</button>
        </div>
      </form>
    </div>
  );
}

export default function DisputesPage() {
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [d, t] = await Promise.all([
        client.get(`/disputes${filter ? `?status=${filter}` : ""}`),
        client.get("/transactions"),
      ]);
      setItems(d.data || []);
      setTransactions((t.data || []).filter((x) => ["COMPLETED", "DISPUTED", "PENDING_FULFILMENT", "FULFILMENT_CONFIRMED"].includes(x.status)));
    } catch (err) { setError(extractError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const create = async (payload) => {
    setBusy(true); setError("");
    try { await client.post("/disputes", payload); setDialog(false); load(); }
    catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  if (detail) return <DisputeDetail id={detail} onBack={() => { setDetail(null); load(); }} />;

  return (
    <div className="my-listings-page" data-testid="disputes-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="dispute-tabs">
          {FILTERS.map((f) => (
            <button key={f.value || "all"} type="button" className={filter === f.value ? "active" : ""}
              data-testid={`dispute-filter-${f.value || "all"}`} onClick={() => setFilter(f.value)}>{f.label}</button>
          ))}
        </div>
        <button type="button" className="primary-button" onClick={() => setDialog(true)} disabled={transactions.length === 0} data-testid="raise-dispute-btn"><ShieldAlert size={13} /> Raise dispute</button>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="disputes-table">
          <thead><tr><th>Type</th><th>Role</th><th>Amount in dispute</th><th>Status</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="empty-row">No disputes yet.</td></tr>}
            {items.map((d) => (
              <tr key={d.id} data-testid={`dispute-row-${d.id}`}>
                <td><strong>{d.dispute_type.replace(/_/g, " ")}</strong><br /><small className="muted">{d.transaction_id.slice(0, 8)}</small></td>
                <td><small>{d.viewer_side}</small></td>
                <td>₹{d.amount_in_dispute.toLocaleString("en-IN")}</td>
                <td><span className={`status-chip status-${d.status.toLowerCase()}`}>{STATUS_LABEL[d.status]}</span></td>
                <td><small>{new Date(d.created_at).toLocaleDateString()}</small></td>
                <td><button type="button" className="link-button" data-testid={`open-dispute-${d.id}`} onClick={() => setDetail(d.id)}>Open →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dialog && <RaiseDialog transactions={transactions} onCancel={() => setDialog(false)} onSubmit={create} busy={busy} />}
    </div>
  );
}
